import type { TimeFrame } from "@domains/engagement/shared/timeFrame.ts";
import { type CalendarDate, type DateDuration, parseDate } from "@internationalized/date";
import type { ReportingScope } from "@packages/core/organization/scope.ts";
import type { FeedbackReportInput } from "#shared/feedbackReportContract.ts";

export const FEEDBACK_TIME_FRAMES = ["30d", "3m", "6m", "quarter", "year"] as const satisfies ReadonlyArray<TimeFrame>;
export type FeedbackTimeFrame = (typeof FEEDBACK_TIME_FRAMES)[number];

export const DEFAULT_FEEDBACK_TIME_FRAME: FeedbackTimeFrame = "30d";

export const CUSTOM_FEEDBACK_PERIOD = "custom";

const FEEDBACK_DATA_FLOOR = parseDate("2025-07-15");

const FEEDBACK_MAX_RANGE: DateDuration = { years: 3 };

export function getFeedbackDataFloor(scope: ReportingScope) {
    return scope === "organization" ? null : FEEDBACK_DATA_FLOOR;
}

export interface FeedbackRangeBounds {
    floor: CalendarDate | null;
    today: CalendarDate;
}

export function exceedsFeedbackMaxRange(start: CalendarDate, end: CalendarDate) {
    return end.compare(start.add(FEEDBACK_MAX_RANGE)) > 0;
}

export interface FeedbackPresetPeriod {
    kind: "preset";
    timeFrame: FeedbackTimeFrame;
}

export interface FeedbackCustomPeriod {
    kind: "custom";
    start: CalendarDate;
    end: CalendarDate;
}

export type FeedbackPeriod = FeedbackPresetPeriod | FeedbackCustomPeriod;

export const DEFAULT_FEEDBACK_PERIOD: FeedbackPresetPeriod = { kind: "preset", timeFrame: DEFAULT_FEEDBACK_TIME_FRAME };

export function isSelectableFeedbackRange(start: CalendarDate, end: CalendarDate, { floor, today }: FeedbackRangeBounds) {
    return start.compare(end) <= 0 && !exceedsFeedbackMaxRange(start, end) && (!floor || start.compare(floor) >= 0) && end.compare(today) <= 0;
}

export interface FeedbackPeriodParams {
    period: FeedbackTimeFrame | typeof CUSTOM_FEEDBACK_PERIOD;
    start: CalendarDate | null;
    end: CalendarDate | null;
}

export function toFeedbackPeriodInput(period: FeedbackPeriod): Pick<FeedbackReportInput, "timeFrame" | "start" | "end"> {
    return period.kind === "preset" ? { timeFrame: period.timeFrame } : { start: period.start.toString(), end: period.end.toString() };
}

export function resolveFeedbackPeriod({ period, start, end }: FeedbackPeriodParams, bounds: FeedbackRangeBounds): FeedbackPeriod {
    if (period !== CUSTOM_FEEDBACK_PERIOD) {
        return { kind: "preset", timeFrame: period };
    }

    if (!start || !end || !isSelectableFeedbackRange(start, end, bounds)) {
        return DEFAULT_FEEDBACK_PERIOD;
    }

    return { kind: "custom", start, end };
}
